/**
 * Public Listing (prospect-facing) — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/prospect/listing.schemas.ts`
 * imports and re-exports these to register the routes, and `apps/prospect/src/lib/api.ts`
 * derives its `RequestShowingInput` / `RequestShowingResponse` (and `PublicListingResponse`)
 * types from the same objects. See packages/api-types/README.md.
 *
 * Covers:
 *   GET  /prospect/api/listings/:publicToken
 *   POST /prospect/api/listings/:publicToken/request-showing
 */
import { z } from 'zod';
// POST .../request-showing body — leadless showing-request intake (BR-PL-05).
export const PublicListingRequestShowingBodySchema = z.object({
    email: z.email(),
    name: z.string().min(1).max(255).optional(),
    phone: z.string().max(50).optional(),
    desiredMoveInDate: z.string().optional(), // ISO date string
    slotStart: z.string(),
});
// POST .../request-showing response.
export const PublicListingRequestShowingResponseSchema = z.object({
    status: z.enum(['proposed', 'confirmed']),
    leadId: z.string(),
    resumed: z.boolean(),
    proposedFor: z.string(),
});
// ---------------------------------------------------------------------------
// GET .../listings/:publicToken response — three-way status union (BR-PL-01).
// 'active' and 'paused' share the identical detail shape; 'unavailable' carries
// no detail (draft/closed/etc.).
// ---------------------------------------------------------------------------
export const PublicListingPhotoSchema = z.object({
    url: z.string(),
    thumbnailUrl: z.string().nullable(),
    caption: z.string().nullable(),
    displayOrder: z.number().int(),
    aiCategory: z.string().nullable(),
});
export const PublicListingScreeningCriteriaSchema = z.object({
    minIncomeMultiple: z.number().nullable(),
    maxOccupants: z.number().int().nullable(),
    smokingPolicy: z.string(),
    minCreditScore: z.number().int().nullable(),
    acceptsHousingVouchers: z.boolean(),
    criminalHistoryLookbackYears: z.number().int().nullable(),
    additionalCriteria: z.string().nullable(),
});
/** One upcoming open house — time window only (BR-112, BR-PL-06): never attendees, RSVPs or capacity. */
export const PublicListingOpenHouseSchema = z.object({
    startsAt: z.string(), // ISO 8601 UTC
    endsAt: z.string(), // ISO 8601 UTC
});
// Shared by 'active' and 'paused' — identical detail shape, BR-PL-01.
const publicListingDetailFields = {
    publicToken: z.string(),
    askingRentCents: z.number().int(),
    availableDate: z.string(),
    updatedAt: z.string(),
    bedrooms: z.number().nullable(),
    bathrooms: z.number().nullable(),
    sqft: z.number().nullable(),
    address: z.object({
        street: z.string(),
        unit: z.string().nullable(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
    }),
    description: z.string().nullable(),
    propertyType: z.string().nullable(),
    amenities: z.array(z.string()),
    acceptedLeaseTerms: z.array(z.string()),
    furnished: z.string(),
    smokingAllowed: z.boolean(),
    utilitiesIncluded: z.array(z.string()),
    fees: z.record(z.string(), z.unknown()).nullable(),
    petPolicy: z.record(z.string(), z.unknown()).nullable(),
    photos: z.array(PublicListingPhotoSchema),
    attributes: z.record(z.string(), z.unknown()).nullable(),
    contact: z.object({
        email: z.string().nullable(),
        phone: z.string().nullable(),
    }),
    openHouses: z.array(PublicListingOpenHouseSchema),
    timezone: z.string().nullable(),
    screeningCriteria: PublicListingScreeningCriteriaSchema.nullable(),
};
/**
 * Active listing response — all fields as returned by mapListingToPublicJson.
 * PRIVACY: no manager.*, no incomeRestrictions (BR-PL-06).
 */
export const PublicListingActiveResponseSchema = z.object({
    status: z.literal('active'),
    ...publicListingDetailFields,
});
/**
 * Paused listing response — same detail shape as active (BR-PL-01): a paused
 * listing is still viewable, just not open to new conversions (BR-PL-10).
 */
export const PublicListingPausedResponseSchema = z.object({
    status: z.literal('paused'),
    ...publicListingDetailFields,
});
export const PublicListingUnavailableResponseSchema = z.object({
    status: z.literal('unavailable'),
});
export const PublicListingResponseSchema = z.union([
    PublicListingActiveResponseSchema,
    PublicListingPausedResponseSchema,
    PublicListingUnavailableResponseSchema,
]);
//# sourceMappingURL=listing.schemas.js.map